import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// This component handles the OAuth callback from Stripe Connect
// URL: /stripe/callback?code=xxx&state=companyId
export default function StripeCallback({ onComplete }) {
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Connecting your Stripe account...');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get URL parameters
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state'); // This is the companyId we passed
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      // Check for errors from Stripe
      if (error) {
        throw new Error(errorDescription || 'Connection was cancelled');
      }

      if (!code) {
        throw new Error('No authorization code received');
      }

      if (!state) {
        throw new Error('Invalid state parameter');
      }

      setMessage('Exchanging authorization code...');

      // Call the Edge Function to exchange the code for an account ID
      const { data, error: fnError } = await supabase.functions.invoke('stripe-connect-callback', {
        body: { code, companyId: state }
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to connect Stripe account');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to connect Stripe account');
      }

      setMessage('Completing setup...');

      // Mark as success
      setStatus('success');
      setMessage(`Stripe account connected successfully!\n\nCharges Enabled: ${data.chargesEnabled ? 'Yes' : 'Not yet'}\nPayouts Enabled: ${data.payoutsEnabled ? 'Yes' : 'Not yet'}\n\n${!data.detailsSubmitted ? 'Complete your Stripe onboarding to start accepting payments.' : 'You can now accept payments!'}`);

      // Redirect after showing success
      setTimeout(() => {
        if (onComplete) {
          onComplete();
        } else {
          window.location.href = '/';
        }
      }, 5000);

    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Failed to connect Stripe account');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        background: '#1a1a1a',
        borderRadius: '16px',
        padding: '3rem',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid #3a3a3a'
      }}>
        {/* Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: status === 'processing' ? '#635bff20' :
                      status === 'success' ? '#4ecca320' : '#e74c3c20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 2rem'
        }}>
          {status === 'processing' && (
            <Loader
              size={40}
              color="#635bff"
              style={{ animation: 'spin 1s linear infinite' }}
            />
          )}
          {status === 'success' && (
            <CheckCircle size={40} color="#4ecca3" />
          )}
          {status === 'error' && (
            <AlertCircle size={40} color="#e74c3c" />
          )}
        </div>

        {/* Title */}
        <h2 style={{
          color: '#e0e0e0',
          margin: '0 0 1rem 0',
          fontSize: '1.5rem'
        }}>
          {status === 'processing' && 'Connecting to Stripe'}
          {status === 'success' && 'Connection Started'}
          {status === 'error' && 'Connection Failed'}
        </h2>

        {/* Message */}
        <p style={{
          color: '#888',
          margin: 0,
          whiteSpace: 'pre-wrap',
          fontSize: '0.95rem',
          lineHeight: 1.6
        }}>
          {message}
        </p>

        {/* Actions */}
        {status === 'error' && (
          <button
            onClick={() => window.location.href = '/'}
            style={{
              marginTop: '2rem',
              padding: '0.75rem 2rem',
              background: '#635bff',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Return to Dashboard
          </button>
        )}

        {status === 'success' && (
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            background: '#4ecca320',
            borderRadius: '8px',
            color: '#4ecca3',
            fontSize: '0.9rem'
          }}>
            Redirecting to dashboard in 5 seconds...
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
